import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { queryKeys } from '../lib/queryClient';
import { snakeToCamel, camelToSnake } from '../lib/utils';

function mapFromDb(row) { return snakeToCamel(row); }
function mapToDb(obj) { return camelToSnake(obj); }

export function useInvoices({ enabled = true } = {}) {
  return useQuery({
    queryKey: queryKeys.invoices.all,
    queryFn: async () => {
      const data = await api.get('/invoices');
      return data.map(mapFromDb);
    },
    enabled,
  });
}

export function useInvoicesByJob(jobId) {
  return useQuery({
    queryKey: queryKeys.invoices.byJob?.(jobId) || ['invoices', 'job', jobId],
    queryFn: async () => {
      const data = await api.get(`/invoices?job_id=${jobId}`);
      return data.map(mapFromDb);
    },
    enabled: !!jobId,
  });
}

export function useCreateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (newInvoice) => {
      const data = await api.post('/invoices', mapToDb(newInvoice));
      return mapFromDb(data);
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: queryKeys.invoices.all });
      if (data.jobId) {
        qc.invalidateQueries({ queryKey: queryKeys.invoices.byJob?.(data.jobId) || ['invoices', 'job', data.jobId] });
      }
      // Audit row is written server-side with a before/after diff
      // (server/routes/crud.js) — logging here as well produced two rows per save.
    },
  });
}

export function useUpdateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (invoice) => {
      // _prevJobId is a client-only hint for cache invalidation on a MOVE —
      // strip it so it never reaches the server whitelist.
      const { id, _prevJobId, ...updates } = invoice;
      const data = await api.put(`/invoices/${id}`, mapToDb(updates));
      return mapFromDb(data);
    },
    onSuccess: (data, variables) => {
      const byJob = (jid) => queryKeys.invoices.byJob?.(jid) || ['invoices', 'job', jid];
      qc.invalidateQueries({ queryKey: queryKeys.invoices.all });
      if (data.jobId) qc.invalidateQueries({ queryKey: byJob(data.jobId) });
      // On a MOVE (job_id changed), also refresh the source job's list + rollups.
      if (variables?._prevJobId && variables._prevJobId !== data.jobId) {
        qc.invalidateQueries({ queryKey: byJob(variables._prevJobId) });
      }
    },
  });
}

export function useDeleteInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (invoiceId) => {
      // Lands in Trash server-side. Refusals (403 not your job, 409 synced to
      // Xero) throw ApiError carrying the server's sentence for the toast.
      return api.del(`/invoices/${invoiceId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.invoices.all });
      // The server resyncs payout lines for job_id and every job_ids entry.
      qc.invalidateQueries({ queryKey: queryKeys.quotes.all });
      qc.invalidateQueries({ queryKey: ['job-earnings-ledger'] });
      qc.invalidateQueries({ queryKey: queryKeys.crewEarnings.all });
      qc.invalidateQueries({ queryKey: queryKeys.actionLog.all });
      qc.invalidateQueries({ queryKey: queryKeys.trash.all });
    },
  });
}
